import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { BoltIcon } from '@heroicons/react/24/outline'
import { BugAntIcon, PauseCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/solid'
import { capitalize, get, omit, sortBy } from 'lodash'
import { useLayoutEffect, useRef, useState } from 'react'
import { QueryObserverResult, useMutation, useQueries, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import styled from 'styled-components'

import GuideModal from '@/components/GuideModal'
import ConfirmDialog from '@/components/TailwindUI/ConfirmDialog'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import Menu, { MenuItem } from '@/components/TailwindUI/Menu'
import Skeleton from '@/components/TailwindUI/Skeleton'
import Toggle from '@/components/TailwindUI/Toggle'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { convertFeatureToVisualWorkflow } from '@/pages/SpaceAppBuilder/FeaturesPage/featureToVisualWorkflow'
import ErrorModal from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/ErrorModal/ErrorModal'
import { validateNodes } from '@/pages/SpaceAppBuilder/FeaturesPage/utils'
import {
  App,
  AppInstanceDataSource,
  Feature,
  FeatureStatus,
  RelatedFeature,
  TriggerTypes,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { clearLocalStorageTriggerData } from '@/pages/SpaceAppBuilder/utilities/localStoragePreviousTriggerData'
import {
  dataSourceV3CatalogQueryOptions,
  dataSourceV3QueryOptions,
  featureQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import cn from '@/utilities/cn'
import { gzip } from '@/utilities/compression'

import CreateFeatureModal from './CreateFeatureModal'
import FeatureRow from './Feature'
import { useWorkflowValidations, useWorkflowValidationsDispatch } from './WorkflowValidationContext'

type Props = {
  appQueryResult: QueryObserverResult<App>
  readOnly?: boolean
  onDeployFinished?: () => void
}

type ErrorModalState = {
  isOpen: boolean
  featureNames: [] | string[]
  errorFrom?: string
}

const ConfirmBulkAction = styled(ConfirmDialog)``

export function sortFeatures(features: Feature[] | undefined) {
  let sortedFeatures = sortBy(features, 'type')

  const parentFeatures = sortedFeatures?.filter(f => f.relatedFeatures?.length)

  parentFeatures?.forEach(parentFeature => {
    parentFeature?.relatedFeatures?.forEach((related: RelatedFeature) => {
      const foundRelated = features?.find(actionFeature => actionFeature.id === related.featureId)
      if (foundRelated) {
        // remove related feature from array
        sortedFeatures = sortedFeatures.filter(feature => feature.id !== foundRelated.id)

        // find index of parent feature
        const parentIndex = sortedFeatures.findIndex(feature => feature.id === parentFeature.id)

        // insert related feature into array next to the parent feature
        sortedFeatures.splice(parentIndex + 1, 0, foundRelated)
      }
    })
  })
  return sortedFeatures
}

export default function Features({ appQueryResult, readOnly = true, onDeployFinished }: Props) {
  const checkbox = useRef()
  const [checked, setChecked] = useState(false)
  const [indeterminate, setIndeterminate] = useState(false)
  const [confirmAction, setConfirmAction] = useState<any>({ isOpen: false })
  const [confirmBulkAction, setConfirmBulkAction] = useState<any>({ isOpen: false })
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [duplicateFeatureId, setDuplicateFeatureId] = useState<string | null>(null)
  const [featureModal, setFeatureModal] = useState<{
    isOpen: boolean
    isEditing: boolean
    formValues: {
      name?: string
    }
    featureId?: string
  }>({
    isOpen: false,
    isEditing: false,
    formValues: {}
  })
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()
  const history = useHistory()
  const location = useLocation<any>()
  const mutateFeature = useMutation({
    mutationKey: ['action']
  })
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]

  const [isNewlyImported] = useState(location.state?.fromImport)
  const [selectedFeatures, setSelectedFeatures] = useState<Feature[]>([])
  const [actionedFeatures, setActionedFeatures] = useState<Feature[]>([])
  const [failedFeatures, setFailedFeatures] = useState<Feature[]>([])
  const dispatchValidation = useWorkflowValidationsDispatch()
  const validations = useWorkflowValidations()
  const queryClient = useQueryClient()
  const features = sortFeatures(appQueryResult.data?.features || [])
  const dataSources = appQueryResult.data?.dataSources
  const [isDebugLogging, setDebugLogging] = useState<boolean>()
  const isLoading = appQueryResult.isLoading || appQueryResult.isRefetching
  const isProcessingActions = actionedFeatures.length > 0
  const isDeployingOrDeactivating = (feature: Feature) =>
    feature.status === FeatureStatus.DEPLOYING || feature.status === FeatureStatus.DEACTIVATING

  type WorkgridCatalogDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridCatalogDatasourceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3CatalogQueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key
      }),
      enabled: !!dataSources
    })) || []
  )

  const workgridDataSources = dataSourcesQuery.reduce(
    (workgridDataSources: WorkgridCatalogDatasource[], dataSource) => {
      if (dataSource.data) {
        workgridDataSources.push(dataSource.data)
      }
      return workgridDataSources
    },
    []
  )

  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourceInstancesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId || ds.key,
        instanceId: ds.id
      }),
      enabled: !!dataSources
    })) || []
  )

  const dataSourceInstances = dataSourceInstancesQuery.reduce(
    (dataSourcesInstances: WorkgridDatasource[], dataSource) => {
      if (dataSource.data) {
        dataSourcesInstances.push(dataSource.data)
      }
      return dataSourcesInstances
    },
    []
  )

  const showLoadingSpinner = isDuplicating || isProcessingActions || features.find(isDeployingOrDeactivating)

  const isEmpty = features.length === 0

  const [isShowingErrorModal, setIsShowingErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    featureNames: [],
    errorFrom: undefined
  })

  async function upsertFeature(values: any, isEdit = false) {
    const featureId = featureModal.featureId
    if (isEdit) {
      const mutatingFeature = features.find(feature => feature.id === featureId)
      if (!mutatingFeature) return
      setActionedFeatures([...actionedFeatures, mutatingFeature])
    }
    const result = await mutateFeature.mutateAsync(
      // @ts-expect-error (someday TS won't be terrible)
      [
        featureQueryOptions({ spaceId, appId, featureId }).queryKey[0],
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(values)
        }
      ],
      {
        onSuccess: async () => {
          await appQueryResult.refetch()
          if (isEdit) {
            setActionedFeatures(actionedFeatures.filter(feature => feature.id === featureId))
          }
        }
      }
    )

    return result
  }

  useQuery({
    ...featureQueryOptions({ spaceId, featureId: duplicateFeatureId, appId }),
    enabled: isDuplicating,

    onSuccess: (originalFeature: Feature) => {
      // Today anytime we ask for a specific feature we simply create a new one with that feature
      const duplicatedFeature = {
        name: originalFeature.name + ' copy',
        trigger: originalFeature.trigger,
        workflow: { ...originalFeature.workflow },
        config: originalFeature.config
      }
      mutateFeature.mutate(
        // @ts-expect-error (someday TS won't be terrible)
        [
          featureQueryOptions({ spaceId, appId }).queryKey[0],
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
              'x-workgrid-accept-encoding': 'workgrid-gzip',
              'x-workgrid-content-encoding': 'workgrid-gzip'
            },
            body: JSON.stringify(gzip(duplicatedFeature))
          }
        ],
        {
          onSuccess: async () => {
            // Reload the features
            await appQueryResult.refetch()
            if (isDuplicating) {
              setIsDuplicating(false)
            }
          }
        }
      )
    }
  })

  useLayoutEffect(() => {
    const isIndeterminate = selectedFeatures.length > 0 && selectedFeatures.length < features.length
    setChecked(selectedFeatures.length === features.length)
    setIndeterminate(isIndeterminate)
    if (checkbox.current) {
      // @ts-expect-error
      checkbox.current.indeterminate = isIndeterminate
    }
  }, [selectedFeatures, features])

  function toggleAll() {
    setSelectedFeatures(
      checked || indeterminate
        ? []
        : features.filter(
            feature =>
              feature.type !== TriggerTypes.notificationAction &&
              feature.status !== FeatureStatus.DEPLOYING &&
              feature.status !== FeatureStatus.DEACTIVATING
          )
    )
    setChecked(!checked && !indeterminate)
    setIndeterminate(false)
  }

  function navigateToHistory(id: string | undefined) {
    if (id) {
      history.push(`${location.pathname}/features/${id}/featureHistory`, { state: { fromAppDetailsPage: true } })
    }
  }

  function navigateToFeature(id: string | undefined) {
    if (id) {
      history.push(`${location.pathname}/features/${id}`, {
        state: {
          fromImport: isNewlyImported,
          shouldValidate: validations?.includes(id)
        }
      })
    }
  }

  async function handleFeatureModalSubmit(values: { name?: string }) {
    setFeatureModal({ ...featureModal, isOpen: false })
    const upsertedFeature: any = await upsertFeature(values, featureModal.isEditing)
    if (upsertedFeature.id && !featureModal.isEditing) {
      navigateToFeature(upsertedFeature.id)
    }
  }

  function handleAdd() {
    setFeatureModal({
      isOpen: true,
      isEditing: false,
      formValues: { name: '' }
    })
  }

  function handleSelect(isChecked: boolean, feature: Feature) {
    if (isChecked) {
      setSelectedFeatures([...selectedFeatures, feature])
    } else {
      setSelectedFeatures(selectedFeatures.filter(f => f !== feature))
    }
  }

  function handleDuplicate(featureId: string | undefined) {
    if (featureId) {
      setIsDuplicating(true)
      setDuplicateFeatureId(featureId)
    }
  }

  function handleRename(feature: Feature) {
    setFeatureModal({
      isOpen: true,
      isEditing: true,
      featureId: feature.id,
      formValues: { name: feature.name }
    })
  }

  async function handleAction(feature: Feature, type: 'delete' | 'deactivate' | 'deploy' | 'debug') {
    dispatchValidation({
      type: 'delete',
      id: feature.id
    })
    let mutation
    let status: 'success' | 'fail' = 'success'

    // Validate the workflows
    if (type === 'deploy') {
      let workflow
      try {
        const result = await queryClient.fetchQuery<Feature>(
          featureQueryOptions({ spaceId, featureId: feature.id, appId })
        )
        workflow = result.workflow
      } catch {}

      if (workflow) {
        const elements = convertFeatureToVisualWorkflow({
          workflow,
          dataSources: workgridDataSources,
          isValidatingFeature: true
        })

        const result = await validateNodes(elements, workflow, true, features)
        if (!result.workflowIsValid) {
          return {
            status: 'fail',
            feature
          }
        }
      }
    }

    switch (type) {
      case 'deploy': {
        mutation = [
          `/v1/appbuilder/spaces/${spaceId}/instances/${appId}/features/${feature.id}/deploy`,
          {
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              isDebugLoggingEnabled: isDebugLogging
            })
          }
        ]

        break
      }
      case 'deactivate': {
        mutation = [
          `/v1/appbuilder/spaces/${spaceId}/instances/${appId}/features/${feature.id}/deactivate`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        ]

        break
      }
      case 'delete': {
        mutation = [featureQueryOptions({ spaceId, appId, featureId: feature.id }).queryKey[0], { method: 'DELETE' }]

        break
      }
      case 'debug': {
        if (feature.status !== 'active') {
          return {
            status,
            feature
          }
        }
        const debugFeature = omit(feature, [
          'id',
          'workflow',
          'createdBy',
          'modifiedBy',
          'createdDate',
          'modifiedDate',
          'audience',
          'templateFeatureId'
        ])
        mutation = [
          `/v1/appbuilder/spaces/${spaceId}/instances/${appId}/features/${feature.id}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...debugFeature,
              isDebugLoggingEnabled: true
            })
          }
        ]
        break
      }
      // No default
    }

    try {
      // @ts-expect-error
      await mutateFeature.mutateAsync(mutation)

      if (
        type === 'delete' && // delete previous trigger data values
        feature.id
      ) {
        clearLocalStorageTriggerData(feature.id)
      }
    } catch {
      mutateFeature.reset()
      status = 'fail'
    }
    return {
      status,
      feature
    }
  }

  async function handleBulkAction(type: 'deactivate' | 'deploy' | 'debug') {
    setConfirmBulkAction({ ...confirmBulkAction, isOpen: false })
    setActionedFeatures([...actionedFeatures, ...selectedFeatures])
    setFailedFeatures(failedFeatures.filter(feature => !selectedFeatures.includes(feature)))
    const results = await Promise.all(selectedFeatures.map(feature => handleAction(feature, type)))
    const newlyFailedFeatures = results.filter(result => result.status === 'fail').map(result => result.feature)
    if (newlyFailedFeatures.length > 0) {
      newlyFailedFeatures.forEach(feature => {
        dispatchValidation({
          type: 'add',
          id: feature.id
        })
      })

      setFailedFeatures([...failedFeatures, ...newlyFailedFeatures])
    }
    if (type === 'deploy') {
      onDeployFinished?.()
    }
    await appQueryResult.refetch()
    setActionedFeatures(actionedFeatures.filter(f => !results?.find(feat => feat.feature.id === f.id)))
    setSelectedFeatures(selectedFeatures.filter(f => !results?.find(feat => feat.feature.id === f.id)))
  }

  async function handleSingleAction(feature: Feature, type: 'deactivate' | 'deploy' | 'delete' | 'debug') {
    setConfirmAction({ ...confirmAction, isOpen: false })
    setActionedFeatures([...actionedFeatures, feature])
    setFailedFeatures(failedFeatures.filter(f => f.id !== feature.id))
    const result = await handleAction(feature, type)
    if (result.status === 'fail') {
      dispatchValidation({
        type: 'add',
        id: result.feature.id
      })
      setFailedFeatures([...failedFeatures, result.feature])
    }
    if (type === 'deploy') {
      onDeployFinished?.()
    }
    await appQueryResult.refetch()
    setActionedFeatures(actionedFeatures.filter(f => f.id !== result.feature.id))
    setSelectedFeatures(selectedFeatures.filter(f => f.id !== result.feature.id))
  }

  const willSendNotificationOnDeploy = (isBulkAction: boolean) => {
    const features = isBulkAction ? selectedFeatures : [confirmAction.feature]
    return (
      dataSourceInstances?.some(dataSource => get(dataSource.configuration, 'auth') === 'oauth2') &&
      features.some(
        feature =>
          (feature.type === TriggerTypes.microappView && feature.isDefault) || feature.type === TriggerTypes.knowledge
      )
    )
  }

  return (
    <>
      <ErrorModal
        isOpen={isShowingErrorModal.isOpen}
        onClose={() => setIsShowingErrorModal({ isOpen: false, featureNames: [], errorFrom: undefined })}
        featureNames={isShowingErrorModal.featureNames}
        errorFrom={isShowingErrorModal.errorFrom}
      />
      <CreateFeatureModal
        formValues={featureModal.formValues}
        onSubmit={handleFeatureModalSubmit}
        isOpen={featureModal.isOpen}
        isEditing={featureModal.isEditing}
        onClose={() => setFeatureModal({ ...featureModal, isOpen: false })}
      />
      <ConfirmBulkAction
        size="sm"
        variant="info"
        isOpen={confirmBulkAction.isOpen}
        onConfirm={() => handleBulkAction(confirmBulkAction.type)}
        onCancel={() => setConfirmBulkAction({ ...confirmBulkAction, isOpen: false })}
        message={`${capitalize(confirmBulkAction.type)} ${agentsFlagOn ? 'Capabilities' : 'Features'}`}
        subMessage={
          <>
            {confirmBulkAction.type === 'debug' && (
              <div className="mt-3">
                <span>Logs are captured for all errors and failures by default.</span>{' '}
                <span className="font-semibold">
                  Debug mode will capture logs for all events in deployed features for 1 day
                </span>
              </div>
            )}
            <div>
              Are you sure you want to {confirmBulkAction.type} the selected{' '}
              {agentsFlagOn ? 'capabilities' : 'features'}?
            </div>
            {confirmBulkAction.type === 'deploy' && (
              <>
                {willSendNotificationOnDeploy(true) && (
                  <div className="mt-3">
                    <span className="font-semibold">
                      This deployment will send a notification to the {agentsFlagOn ? 'agent' : 'app'} audience members
                      who have not authenticated to this {agentsFlagOn ? 'capability' : 'feature'}.
                    </span>{' '}
                  </div>
                )}
                <div className="mt-3">
                  <span className="font-semibold">
                    Before deploying, ensure your workflows are able to run successfully.
                  </span>{' '}
                  <span>If not, deployment could fail. To do this, open each workflow and select "Test".</span>
                </div>
                <div className="mt-6 flex">
                  <Toggle
                    boldHeaders={true}
                    onChange={() => setDebugLogging(prevState => !prevState)}
                    checked={isDebugLogging}
                  />
                  <div className="ml-2 flex items-center">Enable debug execution logging for 1 day</div>
                </div>
              </>
            )}
          </>
        }
      />
      <ConfirmDialog
        size="sm"
        isOpen={confirmAction.isOpen}
        variant={confirmAction.type === 'delete' ? 'error' : 'info'}
        onConfirm={() => handleSingleAction(confirmAction.feature, confirmAction.type)}
        onCancel={() => setConfirmAction({ ...confirmAction, isOpen: false })}
        message={`${capitalize(confirmAction.type)} ${agentsFlagOn ? 'Capability' : 'Feature'}`}
        subMessage={
          <>
            {confirmAction.type === 'debug' && (
              <div className="mt-3">
                <span>Logs are captured for all errors and failures by default.</span>{' '}
                <span className="font-semibold">Debug mode will capture logs for all events for 1 day</span>
              </div>
            )}
            <div>
              <span>Are you sure you want to {confirmAction.type} the</span>{' '}
              <span className="font-bold">{confirmAction.feature?.name}</span>{' '}
              <span>{agentsFlagOn ? 'capability' : 'feature'}?</span>
            </div>
            {confirmAction.type === 'deploy' && (
              <div className="my-6">
                {willSendNotificationOnDeploy(false) && (
                  <div className="mt-3">
                    <span className="font-semibold">
                      This deployment will send a notification to the {agentsFlagOn ? 'agent' : 'app'} audience members
                      who have not authenticated to this {agentsFlagOn ? 'capability' : 'feature'}.
                    </span>{' '}
                  </div>
                )}
                <div className="my-6">
                  <span className="font-semibold">
                    Before deploying, ensure your workflow is able to run successfully.
                  </span>{' '}
                  <span>If not, deployment could fail. To do this, open the workflow and select "Test".</span>
                </div>
                <div className="mt-6 flex">
                  <Toggle
                    boldHeaders={true}
                    onChange={() => setDebugLogging(prevState => !prevState)}
                    checked={isDebugLogging}
                  />
                  <div className="ml-2 flex items-center">Enable debug execution logging for 1 day</div>
                </div>
                {confirmAction.feature.type === TriggerTypes.scheduled && (
                  <div className="my-6">
                    <span className="font-semibold">
                      For user-scheduled {agentsFlagOn ? 'capabilities' : 'features'} with large audiences, expect this
                      process to take 5 minutes per 20,000 users.
                    </span>
                  </div>
                )}
              </div>
            )}
            {confirmAction.type === 'deactivate' && confirmAction.feature.type === TriggerTypes.scheduled && (
              <div className="my-6">
                <span className="font-semibold">
                  For user-scheduled {agentsFlagOn ? 'capabilities' : 'features'} with large audiences, expect this
                  process to take 5 minutes per 20,000 users.
                </span>
              </div>
            )}
            {confirmAction.type === 'delete' && <div className="my-3 font-semibold">This cannot be undone.</div>}
          </>
        }
      />
      <div className="w-full bg-white px-4">
        <div className="mt-4 flex h-10 items-end justify-start sm:justify-between">
          <div className="mr-5 sm:mr-0">
            <h1 className="text-lg font-semibold leading-6 text-gray-900">
              {agentsFlagOn ? 'Capabilities' : 'Features'}
            </h1>
          </div>
          <div className="flex flex-row-reverse items-end space-x-4 space-x-reverse">
            <div className="mt-4">
              {!readOnly && !isEmpty && (
                <button
                  type="button"
                  className="block rounded-md bg-blue-600 py-1.5 px-3 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                  onClick={handleAdd}
                >
                  + Add {agentsFlagOn ? 'capability' : 'feature'}
                </button>
              )}
            </div>
            {selectedFeatures.length > 0 && (
              <Menu
                className={cn({
                  'pointer-events-none': isProcessingActions
                })}
                icon={
                  <div
                    className={cn(
                      'inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                      { 'bg-gray-50': isProcessingActions }
                    )}
                  >
                    Bulk Actions
                    <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                }
              >
                <div className="py-1">
                  {selectedFeatures.find(feature => feature.status === 'active') && (
                    <>
                      <MenuItem onClick={() => setConfirmBulkAction({ isOpen: true, type: 'deactivate' })}>
                        <PauseCircleIcon
                          className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                          aria-hidden="true"
                        />
                        Deactivate
                      </MenuItem>
                      {selectedFeatures.find(
                        feature => feature.isDebugLoggingEnabled === false && feature.status === 'active'
                      ) && (
                        <MenuItem onClick={() => setConfirmBulkAction({ isOpen: true, type: 'debug' })}>
                          <BugAntIcon
                            className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                            aria-hidden="true"
                          />
                          Enable debug
                        </MenuItem>
                      )}
                    </>
                  )}
                  {selectedFeatures.find(feature => feature.status !== 'active') && (
                    <MenuItem onClick={() => setConfirmBulkAction({ isOpen: true, type: 'deploy' })}>
                      <RocketLaunchIcon
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                        aria-hidden="true"
                      />
                      Deploy
                    </MenuItem>
                  )}
                </div>
              </Menu>
            )}
          </div>
        </div>
        <hr className="mt-2" />
        {isNewlyImported && (
          <GuideModal
            isShowingGuide={isNewlyImported}
            header="Install Successful"
            message={
              <>
                <span>{agentsFlagOn ? 'Agent capabilities are in' : 'App features are in'}</span>{' '}
                <span className="font-bold">Draft.</span>
              </>
            }
            subMessage={`Edit a ${agentsFlagOn ? 'capability' : 'feature'} to test and deploy to your workspace.`}
            translate="translate-x-56 translate-y-9"
          />
        )}
        {location.state?.fromAppUpdate && (
          <GuideModal
            isShowingGuide={location.state?.fromAppUpdate}
            header="Update Successful"
            message={
              <>
                <span>
                  Review updated {agentsFlagOn ? 'capabilities' : 'features'}. Any newly added{' '}
                  {agentsFlagOn ? 'capabilities' : 'features'} will be in status{' '}
                </span>{' '}
                <span className="font-bold">Draft.</span>
              </>
            }
            subMessage="Edit a feature to test and deploy to your workspace."
            translate="translate-x-56 translate-y-9"
          />
        )}
        <div className="mt-4 flow-root">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="relative">
                {!isLoading && isEmpty ? (
                  <div className="flex items-center justify-center">
                    <div className="mt-6 flex w-5/6 flex-col items-center justify-center space-y-8">
                      <BoltIcon className="h-20 w-20 stroke-1 text-gray-400" aria-hidden="true" />
                      <p className="mt-4 pl-2 text-base text-gray-500">
                        Add {agentsFlagOn ? 'capabilities' : 'features'} to your {agentsFlagOn ? 'agent' : 'app'} that
                        can be triggered by events
                      </p>
                      {!readOnly && (
                        <button
                          type="button"
                          className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={handleAdd}
                        >
                          Add a {agentsFlagOn ? 'capability' : 'feature'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <table className="min-w-full table-fixed divide-y divide-gray-100">
                    <thead>
                      <tr>
                        <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                          <input
                            type="checkbox"
                            className={cn(
                              'absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600',
                              { hidden: readOnly }
                            )}
                            // @ts-expect-error
                            ref={checkbox}
                            checked={checked}
                            onChange={toggleAll}
                          />
                        </th>
                        <th
                          scope="col"
                          className="min-w-[12rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                        >
                          Name
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Trigger
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Status
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                          {showLoadingSpinner && (
                            <div className="flex items-center justify-center">
                              <LoadingSpinner />
                            </div>
                          )}
                          <span className="sr-only">Edit</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {isLoading ? (
                        <>
                          {[0, 1].map(r => (
                            <tr key={`feature-loader-${r}`} data-testid="loader" className="pointer-events-none">
                              <td className="relative px-7 sm:w-12 sm:px-6">
                                <Skeleton className="h-5" />
                              </td>
                              <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium">
                                <Skeleton className="h-5" />
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <Skeleton className="h-5" />
                              </td>
                              <td className="whitespace-nowrap px-3 py-5 text-sm">
                                <Skeleton className="h-5" />
                              </td>
                              <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"></td>
                            </tr>
                          ))}
                        </>
                      ) : (
                        features.map((feature: Feature) => (
                          <FeatureRow
                            key={feature.id}
                            readOnly={readOnly}
                            feature={feature}
                            isProcessing={
                              actionedFeatures.includes(feature) ||
                              isDeployingOrDeactivating(feature) ||
                              features.some(
                                f =>
                                  isDeployingOrDeactivating(f) &&
                                  f.relatedFeatures?.some(rf => rf.featureId === feature.id)
                              )
                            }
                            isSelected={selectedFeatures.some(
                              f => f.id === feature.id || f.relatedFeatures?.some(rf => rf.featureId === feature.id)
                            )}
                            isCopyable={feature.type !== TriggerTypes.notificationAction}
                            isDeployable={feature.type !== TriggerTypes.notificationAction}
                            isDebug={feature.isDebugLoggingEnabled}
                            isError={validations?.includes(feature.id)}
                            onSelect={isChecked => handleSelect(isChecked, feature)}
                            onEdit={navigateToFeature}
                            onRename={handleRename}
                            onDuplicate={handleDuplicate}
                            onDeactivate={(feature: Feature) => {
                              setConfirmAction({ type: 'deactivate', isOpen: true, feature: feature })
                            }}
                            onDebug={(feature: Feature) => {
                              setConfirmAction({ type: 'debug', isOpen: true, feature: feature })
                            }}
                            onDeploy={(feature: Feature) => {
                              setConfirmAction({ type: 'deploy', isOpen: true, feature: feature })
                            }}
                            onDelete={(feature: Feature) => {
                              setConfirmAction({ type: 'delete', isOpen: true, feature: feature })
                            }}
                            onViewHistory={(id: string | undefined) => navigateToHistory(id)}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
